import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  closingProblems,
  formatMoney,
  INVOICE_DIRECTION_LABELS,
  italianToday,
  YEAR_CHECK_LABELS,
  type YearCheck,
  type YearCheckKey,
  type YearClosingReport,
  type YearClosure,
  type YearsResponse,
} from '@k-management/shared';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useCan, useCurrentUser } from '../../auth/AuthProvider.js';
import { ConfirmDialog } from '../../components/ConfirmDialog.js';
import { usePageTitle } from '../../components/layout/page-chrome.js';
import { apiRequest } from '../../lib/api.js';
import { formatDate, formatTimestamp } from '../../lib/format.js';
import { ReopenYearDialog } from './ReopenYearDialog.js';

export const yearClosingKeys = {
  all: ['treasury', 'year-closures'] as const,
  year: (year: number) => ['treasury', 'year-closures', year] as const,
};

/** Where the items that keep a check red are worked. */
const CHECK_LINKS: Record<YearCheckKey, string> = {
  estratti: '/tesoreria/banche',
  movimenti: '/tesoreria/da-sistemare',
  saldi: '/tesoreria/banche',
  spese_carta: '/tesoreria/da-sistemare',
  file_carte: '/tesoreria/carte',
  fatture: '/tesoreria/da-sistemare',
  autofatture: '/tesoreria/autofatture',
  quadratura: '/tesoreria/mastrini',
};

const CHANGE_LABELS = { nuova: 'nuova', cambiata: 'cambiata', tolta: 'sparita da Gestify' };

function CheckRow({
  check,
  note,
  onNote,
  editable,
}: {
  check: YearCheck;
  note: string;
  onNote: (value: string) => void;
  editable: boolean;
}) {
  return (
    <li className="year-check">
      <span className={`year-check-dot ${check.ok ? 'ok' : check.mandatory ? 'bad' : 'warn'}`}>
        ●
      </span>
      <div>
        <strong>{check.label}</strong>
        {check.mandatory && <span className="pill muted small">obbligatorio</span>}
        <div className="small">{check.detail}</div>
        {editable && !check.ok && !check.mandatory && (
          <label className="small block">
            Eccezione accettata — motivo
            <textarea
              rows={2}
              maxLength={500}
              value={note}
              onChange={(event) => onNote(event.target.value)}
            />
          </label>
        )}
      </div>
      {!check.ok && (
        <Link className="button" to={CHECK_LINKS[check.key]}>
          Apri
        </Link>
      )}
    </li>
  );
}

function ClosureView({ closure }: { closure: YearClosure }) {
  const { snapshot } = closure;
  return (
    <div className="stack">
      <table className="data-table">
        <tbody>
          <tr>
            <td>Movimenti di banca</td>
            <td className="num">{snapshot.movements}</td>
          </tr>
          <tr>
            <td>Spese con carta</td>
            <td className="num">{snapshot.cardSpending}</td>
          </tr>
          <tr>
            <td>Fatture emesse / ricevute</td>
            <td className="num">
              {snapshot.invoices.issued} / {snapshot.invoices.received}
            </td>
          </tr>
          <tr>
            <td>Da incassare al 31/12, passa all’anno dopo</td>
            <td className="num">
              {snapshot.openCarried.issued.count} fatture ·{' '}
              {formatMoney(snapshot.openCarried.issued.amount)}
            </td>
          </tr>
          <tr>
            <td>Da pagare al 31/12, passa all’anno dopo</td>
            <td className="num">
              {snapshot.openCarried.received.count} fatture ·{' '}
              {formatMoney(snapshot.openCarried.received.amount)}
            </td>
          </tr>
          {snapshot.balances.map((balance) => (
            <tr key={balance.accountCode}>
              <td>
                Saldo {balance.accountCode}
                {balance.date && ` (ultimo movimento ${formatDate(balance.date)})`}
              </td>
              <td className="num">{balance.amount ? formatMoney(balance.amount) : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {closure.exceptions.length > 0 && (
        <div>
          <h3>Eccezioni accettate</h3>
          <ul>
            {closure.exceptions.map((item) => (
              <li key={item.check}>
                <strong>{YEAR_CHECK_LABELS[item.check]}</strong>: {item.note}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/**
 * Tesoreria › Chiusura dell'anno (3.10, ADR-029): the checks of a year; with
 * them green, or the differences explained, the CFO closes it and its data no
 * longer change. Reopening is extraordinary: the administrator, with a reason.
 */
export function YearClosingPage() {
  usePageTitle('Chiusura dell’anno');
  const queryClient = useQueryClient();
  const user = useCurrentUser();
  const canClose = useCan('tesoreria.write', 'tesoreria.year_close');
  const [params, setParams] = useSearchParams();
  const current = Number(italianToday().slice(0, 4));
  const years = useQuery({
    queryKey: yearClosingKeys.all,
    queryFn: () => apiRequest<YearsResponse>('/treasury/year-closures'),
  });
  // By default the first past year still open.
  const fallback =
    years.data?.years.find((item) => !item.closed && item.year < current)?.year ?? current - 1;
  const year = Number(params.get('anno') ?? fallback);
  const report = useQuery({
    queryKey: yearClosingKeys.year(year),
    queryFn: () => apiRequest<YearClosingReport>(`/treasury/year-closures/${year}`),
  });
  const [notes, setNotes] = useState<Partial<Record<YearCheckKey, string>>>({});
  const [confirming, setConfirming] = useState(false);
  const [reopening, setReopening] = useState(false);
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['treasury'] });
  const exceptions = Object.entries(notes)
    .filter(([, note]) => note?.trim())
    .map(([check, note]) => ({ check: check as YearCheckKey, note: note!.trim() }));
  const close = useMutation({
    mutationFn: () =>
      apiRequest<YearClosure>('/treasury/year-closures', {
        method: 'POST',
        body: { year, exceptions },
      }),
    onSuccess: async () => {
      setConfirming(false);
      setNotes({});
      await refresh();
    },
  });

  // The year shown is always among the choices, also before the first one with data.
  const listed = years.data?.years ?? [];
  const options = listed.some((item) => item.year === year)
    ? listed
    : [...listed, { year, closed: Boolean(report.data?.closed) }].sort((a, b) => a.year - b.year);
  const data = report.data;
  const problems = data && !data.closed ? closingProblems(data.checks, exceptions) : [];
  const past = year < current;
  const lastClosure = data?.history.find((closure) => !closure.reopenedAt) ?? null;

  return (
    <section className="stack">
      <div className="row between">
        <p className="muted small">
          Quando i controlli sono verdi, o le differenze sono spiegate, l’anno si chiude: da quel
          momento i suoi movimenti, incassi, pagamenti e la classificazione delle fatture non si
          modificano più. Incassare o pagare nell’anno dopo una fattura dell’anno chiuso resta
          possibile.
        </p>
        <label>
          Anno{' '}
          <select value={year} onChange={(event) => setParams({ anno: event.target.value })}>
            {options.map((item) => (
              <option key={item.year} value={item.year}>
                {item.year}
                {item.closed ? ' (chiuso)' : ''}
              </option>
            ))}
          </select>
        </label>
      </div>
      {report.error && <p className="error">{report.error.message}</p>}
      {data && (
        <div className="card stack">
          <h2>
            {data.closed ? (
              <>
                {year} · <span className="ok">chiuso</span>
                {lastClosure &&
                  ` il ${formatTimestamp(lastClosure.closedAt)}${lastClosure.closedBy ? ` da ${lastClosure.closedBy}` : ''}`}
              </>
            ) : (
              `Chiusura del ${year}`
            )}
          </h2>
          {!data.closed && (
            <p className="muted small">
              I controlli obbligatori non ammettono eccezioni. Gli altri si possono accettare
              scrivendo il motivo, che resta nella fotografia della chiusura.
            </p>
          )}
          <ul className="year-checks">
            {data.checks.map((check) => (
              <CheckRow
                key={check.key}
                check={check}
                note={notes[check.key] ?? ''}
                onNote={(value) => setNotes({ ...notes, [check.key]: value })}
                editable={!data.closed && canClose}
              />
            ))}
          </ul>
          {!data.closed && (
            <div className="row between">
              <span className="small error">
                {!past ? 'Si chiude solo un anno passato.' : problems.join(' · ')}
              </span>
              {canClose && (
                <button
                  type="button"
                  className="primary"
                  disabled={!past || problems.length > 0}
                  onClick={() => setConfirming(true)}
                >
                  Chiudi il {year}
                </button>
              )}
            </div>
          )}
          {lastClosure && <ClosureView closure={lastClosure} />}
          {data.closed && user.isAdmin && (
            <div className="row between">
              <span className="muted small">
                Riaprire è un’operazione straordinaria, solo dell’amministratore.
              </span>
              <button type="button" className="danger" onClick={() => setReopening(true)}>
                Riapri il {year}…
              </button>
            </div>
          )}
        </div>
      )}

      {data && data.gestifyChanges.length > 0 && (
        <div className="card">
          <h2>Gestify dopo la chiusura</h2>
          <p className="muted small">
            L’ultima lettura di Gestify ha trovato queste differenze sulle fatture del {year}: non
            sono state applicate. Per applicarle va riaperto l’anno e riletto Gestify.
          </p>
          <ul className="small">
            {data.gestifyChanges.map((item) => (
              <li key={`${item.direction}:${item.number}`}>
                {INVOICE_DIRECTION_LABELS[item.direction]} {item.number}:{' '}
                {CHANGE_LABELS[item.change]}
              </li>
            ))}
          </ul>
        </div>
      )}

      {data && data.history.some((closure) => closure.reopenedAt) && (
        <div className="card">
          <h2>Chiusure precedenti</h2>
          <ul className="small">
            {data.history
              .filter((closure) => closure.reopenedAt)
              .map((closure) => (
                <li key={closure.id}>
                  Chiuso il {formatTimestamp(closure.closedAt)}
                  {closure.closedBy && ` da ${closure.closedBy}`}, riaperto il{' '}
                  {formatTimestamp(closure.reopenedAt!)}
                  {closure.reopenedBy && ` da ${closure.reopenedBy}`}: {closure.reopenReason}
                </li>
              ))}
          </ul>
        </div>
      )}

      {confirming && (
        <ConfirmDialog
          title={`Chiudere il ${year}?`}
          confirmLabel={`Chiudi il ${year}`}
          busy={close.isPending}
          error={close.error?.message}
          onConfirm={() => close.mutate()}
          onCancel={() => setConfirming(false)}
        >
          <p>
            Dopo la chiusura i dati del {year} non si modificano più. Riaprirlo è un’operazione
            straordinaria dell’amministratore.
          </p>
          {exceptions.length > 0 && (
            <p className="small">
              Eccezioni accettate:{' '}
              {exceptions.map((item) => YEAR_CHECK_LABELS[item.check]).join(', ')}.
            </p>
          )}
        </ConfirmDialog>
      )}
      {reopening && (
        <ReopenYearDialog
          year={year}
          onClose={() => setReopening(false)}
          onDone={async () => {
            setReopening(false);
            await refresh();
          }}
        />
      )}
    </section>
  );
}
